import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Save, Trash2, Zap, ArrowRight, PlusCircle, ChevronRight } from "lucide-react";

const TABS = [
    { key: "SALES", label: "Sales Invoice", desc: "Money you'll receive" },
    { key: "PURCHASE", label: "Purchase Bill", desc: "Money you owe" },
    { key: "RECEIPT", label: "Receipt", desc: "Money received" },
    { key: "PAYMENT", label: "Payment", desc: "Money paid" },
    { key: "JOURNAL", label: "Journal Voucher", desc: "Adjustment / accrual" },
    { key: "CONTRA", label: "Contra", desc: "Bank ↔ Bank / Cash" },
];

const BANKS = [
    { id: "B-01", label: "HDFC Current · 5010XXXX4471" },
    { id: "B-02", label: "SBI Current · 3324XXXX1198" },
    { id: "B-03", label: "ICICI CC · 0012XXXX8804" },
    { id: "B-04", label: "Axis OD · 9107XXXX3341" },
    { id: "B-05", label: "BoB Current · 6620XXXX7712" },
    { id: "CASH", label: "Cash in Hand" },
];

const EXPENSE_ACCOUNTS = [
    { code: "4001", name: "Direct Materials" },
    { code: "4002", name: "Direct Labour" },
    { code: "4003", name: "Subcontractor Charges" },
    { code: "4004", name: "Equipment Running Cost" },
    { code: "4005", name: "Site Overheads" },
    { code: "5001", name: "Employee Salaries" },
    { code: "5002", name: "Office Rent & Utilities" },
    { code: "5003", name: "Interest on Loans" },
    { code: "5005", name: "Other Admin Expenses" },
    { code: "1200", name: "Inventory - Materials" },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function QuickEntry() {
    const [tab, setTab] = useState("SALES");
    const [saving, setSaving] = useState(false);
    const [clients, setClients] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [projects, setProjects] = useState([]);
    const [vouchers, setVouchers] = useState([]);
    const [openInvoices, setOpenInvoices] = useState({ receivables: [], payables: [] });
    const [form, setForm] = useState(() => defaults("SALES"));

    const refresh = async () => {
        const [c, v, p, vs, ar, ap] = await Promise.all([
            api.get("/entry/clients"),
            api.get("/entry/vendors"),
            api.get("/projects"),
            api.get("/entry/vouchers"),
            api.get("/receivables"),
            api.get("/payables"),
        ]);
        setClients(c.data.clients);
        setVendors(v.data.vendors);
        setProjects(p.data.projects);
        setVouchers(vs.data.vouchers);
        setOpenInvoices({
            receivables: ar.data.invoices.filter((i) => i.outstanding > 0),
            payables: ap.data.invoices.filter((i) => i.outstanding > 0),
        });
    };

    useEffect(() => { refresh(); }, []);
    useEffect(() => { setForm(defaults(tab)); }, [tab]);

    function defaults(type) {
        const base = { date: today(), amount: "", narration: "" };
        if (type === "SALES") return { ...base, party_id: "", project_id: "", invoice_no: "", due_date: "", gst_rate: 18, tds_rate: 2 };
        if (type === "PURCHASE") return { ...base, party_id: "", project_id: "", invoice_no: "", due_date: "", gst_rate: 18, tds_rate: 2, expense_code: "4001" };
        if (type === "RECEIPT") return { ...base, bank_id: "B-01", linked_voucher_id: "", party_name: "" };
        if (type === "PAYMENT") return { ...base, bank_id: "B-01", linked_voucher_id: "", party_name: "", tds_rate: 0 };
        if (type === "CONTRA") return { ...base, from_bank_id: "B-01", to_bank_id: "CASH" };
        if (type === "JOURNAL") return { ...base, lines: [
            { account_code: "5005", account_name: "Other Admin Expenses", debit: "", credit: "" },
            { account_code: "1010", account_name: "Bank - HDFC Current", debit: "", credit: "" },
        ] };
        return base;
    }

    const submit = async () => {
        try {
            setSaving(true);
            const amount = Number(form.amount || 0);
            const payload = { ...form, amount };
            if (tab === "SALES" || tab === "PURCHASE") {
                payload.gst_amount = amount * (Number(form.gst_rate || 0) / 100);
                payload.tds_amount = amount * (Number(form.tds_rate || 0) / 100);
                const client = clients.find((c) => c.id === form.party_id);
                const vendor = vendors.find((v) => v.id === form.party_id);
                payload.party_type = tab === "SALES" ? "Client" : "Vendor";
                payload.party_name = client?.name || vendor?.name || "";
                if (form.project_id) {
                    const p = projects.find((x) => x.id === form.project_id);
                    payload.project_code = p?.code || "";
                }
                if (tab === "PURCHASE") {
                    const e = EXPENSE_ACCOUNTS.find((x) => x.code === form.expense_code);
                    payload.expense_name = e?.name || "Direct Materials";
                }
            }
            if (tab === "PAYMENT") {
                payload.tds_amount = amount * (Number(form.tds_rate || 0) / 100);
            }
            if (tab === "JOURNAL") {
                payload.lines = form.lines.map((l) => ({
                    account_code: l.account_code,
                    account_name: l.account_name,
                    debit: Number(l.debit || 0),
                    credit: Number(l.credit || 0),
                }));
                const td = payload.lines.reduce((s, l) => s + l.debit, 0);
                const tc = payload.lines.reduce((s, l) => s + l.credit, 0);
                if (Math.abs(td - tc) > 0.5) {
                    toast.error(`Journal not balanced: Dr ${td.toFixed(0)} vs Cr ${tc.toFixed(0)}`);
                    setSaving(false);
                    return;
                }
                payload.amount = td;
            }
            const res = await api.post(`/entry/${tab}`, payload);
            toast.success(`${res.data.voucher_no} posted · Auto-updated all linked modules`);
            setForm(defaults(tab));
            await refresh();
        } catch (e) {
            toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
        } finally {
            setSaving(false);
        }
    };

    const del = async (id) => {
        if (!confirm("Delete this voucher? This will revert its impact on all linked modules.")) return;
        try {
            await api.delete(`/entry/vouchers/${id}`);
            toast.success("Voucher deleted");
            await refresh();
        } catch (e) {
            toast.error(`Delete failed: ${e.message}`);
        }
    };

    const impactChips = useMemo(() => impactMap(tab), [tab]);

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// QUICK ENTRY · UNIFIED VOUCHER CONSOLE</div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3"><Zap className="text-[var(--brand-amber)]" size={26} /> One Entry, Every Module</h1>
                <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                    Post once → cascades to Receivables · Payables · Banking · Journals · Ledger · Statements · Audit
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="qe-count" label="Total Vouchers Posted" value={vouchers.length} format="custom" accent />
                <KpiCard testId="qe-sales" label="Sales Booked" value={vouchers.filter((v) => v.voucher_type === "SALES").reduce((s, v) => s + (v.amount || 0), 0)} />
                <KpiCard testId="qe-purchase" label="Purchases Booked" value={vouchers.filter((v) => v.voucher_type === "PURCHASE").reduce((s, v) => s + (v.amount || 0), 0)} />
                <KpiCard testId="qe-money-flow" label="Net Bank Movement" value={vouchers.filter((v) => v.voucher_type === "RECEIPT").reduce((s, v) => s + v.amount, 0) - vouchers.filter((v) => v.voucher_type === "PAYMENT").reduce((s, v) => s + v.amount, 0)} sub="Receipts − Payments" />
            </div>

            {/* Tab strip */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        data-testid={`qe-tab-${t.key.toLowerCase()}`}
                        onClick={() => setTab(t.key)}
                        className={`border p-3 text-left transition-colors ${tab === t.key ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)]" : "border-panel hover:border-[var(--brand-amber)]"}`}
                    >
                        <div className={`text-xs font-mono-fin uppercase tracking-[0.15em] ${tab === t.key ? "text-[var(--brand-amber)]" : "text-[var(--text-primary)]"}`}>{t.label}</div>
                        <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] mt-1 uppercase tracking-wider">{t.desc}</div>
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                {/* Form */}
                <Panel testId="qe-form" className="xl:col-span-2" title={`${TABS.find((t) => t.key === tab)?.label} — New Entry`} subtitle="Fill in once. The engine derives balanced journal lines and updates every downstream module.">
                    <VoucherForm
                        tab={tab}
                        form={form}
                        setForm={setForm}
                        clients={clients}
                        vendors={vendors}
                        projects={projects}
                        openInvoices={openInvoices}
                    />
                    <div className="border-t border-panel mt-4 pt-4 flex items-center justify-between">
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                            Impact:
                            {impactChips.map((c) => (
                                <span key={c} className="ml-2 px-2 py-1 border border-panel text-[var(--brand-amber)]">{c}</span>
                            ))}
                        </div>
                        <button
                            data-testid="qe-submit"
                            disabled={saving}
                            onClick={submit}
                            className="px-4 py-2 bg-[var(--brand-amber)] text-black font-mono-fin uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[var(--bg-panel-hover)] transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                            <Save size={12} /> {saving ? "Posting..." : "Post & Cascade"}
                        </button>
                    </div>
                </Panel>

                {/* Impact preview */}
                <Panel testId="qe-impact" title="Cross-Module Impact" subtitle="Where this entry lands automatically">
                    <ImpactMap tab={tab} />
                </Panel>
            </div>

            {/* Vouchers list */}
            <Panel testId="qe-list" title="Recent Vouchers · Live Ledger" subtitle={`${vouchers.length} posted · All persisted to database`}>
                {vouchers.length === 0 ? (
                    <div className="text-center py-8 text-[var(--text-secondary)] font-mono-fin text-xs uppercase tracking-wider">
                        No vouchers yet. Post your first entry above — it will appear here and in every other module.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2">Voucher</th>
                                    <th className="text-left">Date</th>
                                    <th className="text-left">Type</th>
                                    <th className="text-left">Party / Narration</th>
                                    <th className="text-right">Amount</th>
                                    <th className="text-right">Dr = Cr</th>
                                    <th className="text-left">Status</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {vouchers.map((v) => (
                                    <tr key={v.id} data-testid={`qe-row-${v.id}`} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                        <td className="py-2 text-[var(--brand-amber)]">{v.voucher_no}</td>
                                        <td className="text-[var(--text-secondary)]">{fmtDate(v.date)}</td>
                                        <td>
                                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-panel text-[var(--text-primary)]">{v.voucher_type}</span>
                                        </td>
                                        <td className="text-[var(--text-primary)] truncate max-w-xs">{v.party_name || "—"} · <span className="text-[var(--text-muted)]">{v.narration}</span></td>
                                        <td className="text-right text-amber">{fmtINR(v.amount)}</td>
                                        <td className={`text-right ${v.balanced ? "text-pos" : "text-neg"}`}>{fmtINR(v.total_debit)} / {fmtINR(v.total_credit)}</td>
                                        <td><span className="text-[10px] uppercase tracking-wider text-pos">✓ {v.status}</span></td>
                                        <td>
                                            <button
                                                data-testid={`qe-delete-${v.id}`}
                                                onClick={() => del(v.id)}
                                                className="text-[var(--text-muted)] hover:text-neg"
                                                title="Delete & revert cascade"
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
        </div>
    );
}


function VoucherForm({ tab, form, setForm, clients, vendors, projects, openInvoices }) {
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const gross = tab === "SALES" || tab === "PURCHASE" ? Number(form.amount || 0) * (1 + Number(form.gst_rate || 0) / 100) : 0;
    const gst_amt = tab === "SALES" || tab === "PURCHASE" ? Number(form.amount || 0) * (Number(form.gst_rate || 0) / 100) : 0;
    const tds_amt = tab === "SALES" || tab === "PURCHASE" || tab === "PAYMENT" ? Number(form.amount || 0) * (Number(form.tds_rate || 0) / 100) : 0;

    const Row = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;

    const Field = ({ label, children, required }) => (
        <div>
            <label className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--text-secondary)] block mb-1">
                {label}{required && <span className="text-neg ml-1">*</span>}
            </label>
            {children}
        </div>
    );

    const inputCls = "w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin text-[var(--text-primary)] focus:border-[var(--brand-amber)] outline-none";

    return (
        <div className="space-y-3">
            <Row>
                <Field label="Date" required>
                    <input data-testid="qe-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} />
                </Field>
                {(tab === "SALES" || tab === "PURCHASE") && (
                    <Field label={tab === "SALES" ? "Client" : "Vendor"} required>
                        <select data-testid="qe-party" value={form.party_id} onChange={(e) => set("party_id", e.target.value)} className={inputCls}>
                            <option value="">-- Select --</option>
                            {(tab === "SALES" ? clients : vendors).map((p) => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </Field>
                )}
                {(tab === "RECEIPT" || tab === "PAYMENT") && (
                    <Field label="Party (free-text)">
                        <input data-testid="qe-party-name" value={form.party_name} onChange={(e) => set("party_name", e.target.value)} placeholder="e.g. MP Metro / UltraTech" className={inputCls} />
                    </Field>
                )}
            </Row>

            <Row>
                <Field label="Base Amount (₹)" required>
                    <input data-testid="qe-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="0.00" className={inputCls} />
                </Field>
                {(tab === "SALES" || tab === "PURCHASE") && (
                    <>
                        <Field label="Project (optional)">
                            <select data-testid="qe-project" value={form.project_id} onChange={(e) => set("project_id", e.target.value)} className={inputCls}>
                                <option value="">-- No project --</option>
                                {projects.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name}</option>)}
                            </select>
                        </Field>
                    </>
                )}
            </Row>

            {(tab === "SALES" || tab === "PURCHASE") && (
                <Row>
                    <Field label="Bill / Invoice No" required>
                        <input data-testid="qe-invoice" value={form.invoice_no} onChange={(e) => set("invoice_no", e.target.value)} placeholder={tab === "SALES" ? "AWC/25-26/xxx" : "Vendor's Bill No"} className={inputCls} />
                    </Field>
                    <Field label="Due Date" required>
                        <input data-testid="qe-due" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className={inputCls} />
                    </Field>
                </Row>
            )}

            {(tab === "SALES" || tab === "PURCHASE") && (
                <Row>
                    <Field label="GST Rate (%)">
                        <select data-testid="qe-gst" value={form.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} className={inputCls}>
                            {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}
                        </select>
                    </Field>
                    <Field label="TDS Rate (%)">
                        <select data-testid="qe-tds" value={form.tds_rate} onChange={(e) => set("tds_rate", e.target.value)} className={inputCls}>
                            {[0, 1, 2, 5, 10].map((r) => <option key={r} value={r}>{r}%</option>)}
                        </select>
                    </Field>
                </Row>
            )}

            {tab === "PURCHASE" && (
                <Field label="Expense Account (Dr side)">
                    <select data-testid="qe-expense" value={form.expense_code} onChange={(e) => set("expense_code", e.target.value)} className={inputCls}>
                        {EXPENSE_ACCOUNTS.map((e) => <option key={e.code} value={e.code}>{e.code} · {e.name}</option>)}
                    </select>
                </Field>
            )}

            {(tab === "RECEIPT" || tab === "PAYMENT") && (
                <Row>
                    <Field label="Bank / Cash" required>
                        <select data-testid="qe-bank" value={form.bank_id} onChange={(e) => set("bank_id", e.target.value)} className={inputCls}>
                            {BANKS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                        </select>
                    </Field>
                    <Field label={tab === "RECEIPT" ? "Settle Against Invoice (optional)" : "Settle Against Bill (optional)"}>
                        <select data-testid="qe-link" value={form.linked_voucher_id} onChange={(e) => set("linked_voucher_id", e.target.value)} className={inputCls}>
                            <option value="">-- On Account --</option>
                            {(tab === "RECEIPT" ? openInvoices.receivables : openInvoices.payables).map((i) => (
                                <option key={i.id} value={i.id}>{i.invoice_no} · ₹{(i.outstanding / 100000).toFixed(1)}L outstanding</option>
                            ))}
                        </select>
                    </Field>
                </Row>
            )}

            {tab === "PAYMENT" && (
                <Field label="TDS Rate (%) at Source">
                    <select data-testid="qe-tds" value={form.tds_rate} onChange={(e) => set("tds_rate", e.target.value)} className={inputCls}>
                        {[0, 1, 2, 5, 10].map((r) => <option key={r} value={r}>{r}%</option>)}
                    </select>
                </Field>
            )}

            {tab === "CONTRA" && (
                <Row>
                    <Field label="From Account" required>
                        <select data-testid="qe-from" value={form.from_bank_id} onChange={(e) => set("from_bank_id", e.target.value)} className={inputCls}>
                            {BANKS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                        </select>
                    </Field>
                    <Field label="To Account" required>
                        <select data-testid="qe-to" value={form.to_bank_id} onChange={(e) => set("to_bank_id", e.target.value)} className={inputCls}>
                            {BANKS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                        </select>
                    </Field>
                </Row>
            )}

            {tab === "JOURNAL" && (
                <div>
                    <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--text-secondary)] mb-2">Journal Lines (must balance)</div>
                    <table className="w-full font-mono-fin text-xs">
                        <thead className="border-b border-panel">
                            <tr className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">
                                <th className="text-left py-1">Account Code</th>
                                <th className="text-left">Account Name</th>
                                <th className="text-right">Debit</th>
                                <th className="text-right">Credit</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {form.lines.map((ln, i) => (
                                <tr key={i} className="border-b border-[var(--border)]/40">
                                    <td className="py-1">
                                        <input value={ln.account_code} onChange={(e) => setForm((f) => { const ns = [...f.lines]; ns[i] = { ...ns[i], account_code: e.target.value }; return { ...f, lines: ns }; })} className="w-24 bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs" />
                                    </td>
                                    <td>
                                        <input value={ln.account_name} onChange={(e) => setForm((f) => { const ns = [...f.lines]; ns[i] = { ...ns[i], account_name: e.target.value }; return { ...f, lines: ns }; })} className="w-full bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs" />
                                    </td>
                                    <td>
                                        <input type="number" value={ln.debit} onChange={(e) => setForm((f) => { const ns = [...f.lines]; ns[i] = { ...ns[i], debit: e.target.value, credit: e.target.value ? 0 : ns[i].credit }; return { ...f, lines: ns }; })} className="w-28 bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs text-right" />
                                    </td>
                                    <td>
                                        <input type="number" value={ln.credit} onChange={(e) => setForm((f) => { const ns = [...f.lines]; ns[i] = { ...ns[i], credit: e.target.value, debit: e.target.value ? 0 : ns[i].debit }; return { ...f, lines: ns }; })} className="w-28 bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs text-right" />
                                    </td>
                                    <td className="text-right">
                                        {form.lines.length > 2 && (
                                            <button data-testid={`qe-jv-del-${i}`} onClick={() => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }))} className="text-neg"><Trash2 size={11} /></button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button
                        data-testid="qe-jv-add"
                        onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, { account_code: "", account_name: "", debit: "", credit: "" }] }))}
                        className="mt-2 text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] flex items-center gap-1"
                    >
                        <PlusCircle size={11} /> Add Line
                    </button>
                </div>
            )}

            <Field label="Narration / Description" required>
                <textarea data-testid="qe-narration" value={form.narration} onChange={(e) => set("narration", e.target.value)} placeholder="Brief explanation of the entry" rows={2} className={inputCls} />
            </Field>

            {(tab === "SALES" || tab === "PURCHASE") && Number(form.amount) > 0 && (
                <div className="border border-panel p-3 bg-[var(--bg-panel-hover)] grid grid-cols-2 md:grid-cols-4 gap-3 font-mono-fin text-xs">
                    <div><div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Base</div><div className="text-[var(--text-primary)] mt-1">{fmtINR(Number(form.amount))}</div></div>
                    <div><div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">+ GST @ {form.gst_rate}%</div><div className="text-amber mt-1">{fmtINR(gst_amt)}</div></div>
                    <div><div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">− TDS @ {form.tds_rate}%</div><div className="text-neg mt-1">{fmtINR(tds_amt)}</div></div>
                    <div><div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{tab === "SALES" ? "Net Receivable" : "Net Payable"}</div><div className="text-pos mt-1 font-semibold">{fmtINR(gross - tds_amt)}</div></div>
                </div>
            )}

            {tab === "PAYMENT" && Number(form.amount) > 0 && (
                <div className="border border-panel p-3 bg-[var(--bg-panel-hover)] grid grid-cols-3 gap-3 font-mono-fin text-xs">
                    <div><div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Gross Bill</div><div className="text-[var(--text-primary)] mt-1">{fmtINR(Number(form.amount))}</div></div>
                    <div><div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">− TDS @ {form.tds_rate}%</div><div className="text-neg mt-1">{fmtINR(tds_amt)}</div></div>
                    <div><div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Net Bank Debit</div><div className="text-amber mt-1 font-semibold">{fmtINR(Number(form.amount) - tds_amt)}</div></div>
                </div>
            )}
        </div>
    );
}


function impactMap(tab) {
    const map = {
        SALES: ["+ Receivables", "+ Revenue", "+ GST Output", "+ Journals", "+ Ledger"],
        PURCHASE: ["+ Payables", "+ Expense/Inventory", "+ GST Input", "+ TDS Payable", "+ Journals"],
        RECEIPT: ["+ Bank/Cash", "− Receivables", "+ Bank Ledger", "+ Journals"],
        PAYMENT: ["− Bank/Cash", "− Payables", "+ TDS Payable", "+ Bank Ledger"],
        JOURNAL: ["+ Ledger direct", "+ Journals", "+ Statements"],
        CONTRA: ["Bank ↔ Bank/Cash", "+ Bank Ledger"],
    };
    return map[tab] || [];
}


function ImpactMap({ tab }) {
    const routes = {
        SALES: [
            { to: "/receivables", label: "Receivables", why: "New open invoice appears" },
            { to: "/statements", label: "P&L / Revenue", why: "Contract Revenue increased" },
            { to: "/journals", label: "Journal Register", why: "Auto-derived Dr AR / Cr Sales" },
            { to: "/ledger", label: "General Ledger", why: "1100 Debtors + 3001 Revenue posted" },
            { to: "/audit", label: "Audit Checks", why: "AR ↔ GL tie re-verified" },
        ],
        PURCHASE: [
            { to: "/payables", label: "Payables", why: "New open bill appears" },
            { to: "/statements", label: "P&L / Expense", why: "Direct Materials or chosen head" },
            { to: "/journals", label: "Journal Register", why: "Dr Expense + Dr GST / Cr AP" },
            { to: "/ledger", label: "General Ledger", why: "2200 Payables + 1350 GST ITC posted" },
            { to: "/audit", label: "Audit Checks", why: "AP ↔ GL tie re-verified" },
        ],
        RECEIPT: [
            { to: "/banking", label: "Banking Ledger", why: "New Credit transaction" },
            { to: "/receivables", label: "Receivables", why: "Selected invoice outstanding reduced" },
            { to: "/cash", label: "Cash Book", why: "If Cash A/c selected" },
            { to: "/reconciliation", label: "Bank Recon", why: "Book Only until matched" },
        ],
        PAYMENT: [
            { to: "/banking", label: "Banking Ledger", why: "New Debit transaction" },
            { to: "/payables", label: "Payables", why: "Selected bill outstanding reduced" },
            { to: "/compliance", label: "Compliance", why: "TDS Payable increased" },
        ],
        JOURNAL: [
            { to: "/journals", label: "Journal Register", why: "New JV appears" },
            { to: "/ledger", label: "General Ledger", why: "Each line posted to its account" },
            { to: "/statements", label: "Financial Statements", why: "P&L / BS updated" },
        ],
        CONTRA: [
            { to: "/banking", label: "Banking Ledger", why: "Both banks show entries" },
            { to: "/cash", label: "Cash Book", why: "If Cash leg selected" },
        ],
    };
    const items = routes[tab] || [];
    return (
        <div className="space-y-2">
            {items.map((i) => (
                <Link
                    key={i.to}
                    to={i.to}
                    data-testid={`qe-impact-${i.to.replace("/", "")}`}
                    className="flex items-center justify-between border border-panel p-2.5 hover:border-[var(--brand-amber)] hover:bg-[var(--bg-panel-hover)] transition-colors group"
                >
                    <div>
                        <div className="text-xs font-body text-[var(--text-primary)] group-hover:text-[var(--brand-amber)]">{i.label}</div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] mt-0.5">{i.why}</div>
                    </div>
                    <ChevronRight size={12} className="text-[var(--text-muted)] group-hover:text-[var(--brand-amber)]" />
                </Link>
            ))}
        </div>
    );
}
